import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';

interface SafeMarkdownProps {
  children: string;
  className?: string;
}

export default function SafeMarkdown({ children, className = '' }: SafeMarkdownProps) {
  return (
    <div className={className}>
      <ReactMarkdown 
        remarkPlugins={[remarkGfm]} 
        rehypePlugins={[rehypeSanitize]}
        components={{
        // Custom styling for better integration with Tailwind
        p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
        strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
        em: ({ children }) => <em className="italic">{children}</em>,
        code: ({ children, className }) => {
          const isInline = !className;
          if (isInline) {
            return <code className="bg-gray-200 dark:bg-gray-700 px-1 py-0.5 rounded text-sm font-mono">{children}</code>;
          }
          return <code className={className}>{children}</code>;
        },
        pre: ({ children }) => <pre className="bg-gray-100 dark:bg-gray-800 p-4 rounded-lg overflow-x-auto mb-4">{children}</pre>,
        ul: ({ children }) => <ul className="list-disc list-outside mb-4 space-y-2 ml-4">{children}</ul>,
        ol: ({ children }) => <ol className="list-decimal list-outside mb-4 space-y-2 ml-4">{children}</ol>,
        li: ({ children }) => <li className="mb-1 pl-1">{children}</li>,
        h1: ({ children }) => <h1 className="text-2xl font-bold mb-4 mt-6 first:mt-0">{children}</h1>,
        h2: ({ children }) => <h2 className="text-xl font-bold mb-3 mt-5 first:mt-0">{children}</h2>,
        h3: ({ children }) => <h3 className="text-lg font-bold mb-2 mt-4 first:mt-0">{children}</h3>,
        h4: ({ children }) => <h4 className="text-base font-bold mb-2 mt-3 first:mt-0">{children}</h4>,
        h5: ({ children }) => <h5 className="text-sm font-bold mb-2 mt-3 first:mt-0">{children}</h5>,
        h6: ({ children }) => <h6 className="text-xs font-bold mb-2 mt-3 first:mt-0">{children}</h6>,
        blockquote: ({ children }) => <blockquote className="border-l-4 border-gray-300 pl-4 italic mb-4">{children}</blockquote>,
        a: ({ children, href }) => <a href={href} className="text-blue-600 hover:text-blue-800 underline" target="_blank" rel="noopener noreferrer">{children}</a>,
        table: ({ children }) => <table className="min-w-full border-collapse border border-gray-300 mb-4">{children}</table>,
        th: ({ children }) => <th className="border border-gray-300 px-4 py-2 bg-gray-100 font-semibold text-left">{children}</th>,
        td: ({ children }) => <td className="border border-gray-300 px-4 py-2">{children}</td>,
        hr: () => <hr className="my-6 border-gray-300" />,
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
}
